import React, { useState, useRef, useEffect } from 'react';
import { useLearningTimingPause } from '../../hooks/useForegroundTiming';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useChatStore } from '../../store/chatStore';
import { ChatSidebar } from './ChatSidebar';
import { LLMConfigModal } from '../LLM/LLMConfigModal';
import { MarkdownRenderer } from './MarkdownRenderer';
import glassTheme from '../../styles/glassTheme';
import { STATS_TOPIC_CATEGORIES, STATS_TOPIC_TOTAL, buildTopicRequest } from '../../data/statsTopics';

// Shown before the student searches or picks a category: a spread across
// descriptive, inferential, measurement and interpretation themes.
const STARTER_TOPICS = [
  'p值的含义',
  '中心极限定理',
  '标准误',
  '第一类错误',
  'Cohen\'s d',
  '交互作用'
];

const ChatWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$sidebarOpen' && prop !== '$fullscreen',
})<{ $sidebarOpen: boolean; $fullscreen: boolean }>`
  display: flex;
  position: ${props => props.$fullscreen ? 'fixed' : 'relative'};
  ${props => props.$fullscreen ? 'inset: 0; top: 0; left: 0; right: 0; bottom: 0;' : ''}
  z-index: ${props => props.$fullscreen ? 10050 : 'auto'};
  width: 100%;
  /* Only the max cap was raised: on a 900px viewport 68dvh still wins, so short
     screens are unchanged, while tall monitors are no longer capped at 760px. */
  height: ${props => props.$fullscreen ? '100dvh' : 'clamp(560px, 68dvh, 1040px)'};
  min-height: 0;
  ${props => props.$fullscreen
    ? 'background: #0b0d17; backdrop-filter: none; -webkit-backdrop-filter: none; border: none;'
    : glassTheme.glass.light}
  border-radius: ${props => props.$fullscreen ? '0' : glassTheme.borderRadius.lg};
  overflow: hidden;

  @media (max-width: 768px) {
    height: ${props => props.$fullscreen ? '100dvh' : 'min(680px, 72dvh)'};
    min-height: ${props => props.$fullscreen ? '0' : '560px'};

    &::after {
      content: '';
      display: ${props => props.$sidebarOpen ? 'block' : 'none'};
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.7);
      z-index: 4;
      pointer-events: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &, * {
      animation: none !important;
      transition-duration: 0ms !important;
    }
  }
`;

const ChatMainArea = styled.div<{ $sidebarOpen: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;
  min-height: 0;
  transition: all 0.3s ease;
`;

const MobileSidebarBackdrop = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: absolute;
    inset: 0;
    z-index: 4;
    border: 0;
    background: rgba(0, 0, 0, 0.7);
  }
`;

const TitleBar = styled.div`
  height: 56px;
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid ${glassTheme.colors.border};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;

const TitleBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CollapseSidebarButton = styled.button`
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.2s ease;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const TitleBarCenter = styled.div`
  flex: 1;
  text-align: center;
`;

const TitleBarTitle = styled.h1`
  font-size: 1rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin: 0;
`;

const TitleBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

interface MessagesContainerProps {}

const MessagesContainer = styled.div<MessagesContainerProps>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${glassTheme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.5);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(102, 126, 234, 0.7);
  }
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  width: min(100%, 800px);
  max-width: ${props => props.$isUser ? '80%' : '800px'};
  padding: 1rem 1.2rem;
  border-radius: 18px;
  line-height: 1.6;
  font-size: 1rem;
  word-wrap: break-word;
  align-self: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  background: ${props => props.$isUser
    ? glassTheme.gradients.cyber
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$isUser ? 'white' : 'rgba(255, 255, 255, 0.9)'};
  border: ${props => props.$isUser ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'};
  animation: messageSlideIn 0.3s ease;

  @keyframes messageSlideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  code {
    background: rgba(0, 0, 0, 0.3);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    color: #4ade80;
  }

  pre {
    background: rgba(0, 0, 0, 0.4);
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0.5rem 0;

    code {
      background: none;
      padding: 0;
      color: #d4d4d4;
    }
  }

  p {
    margin: 0.5rem 0;
  }

  ul, ol {
    margin-left: 1.5rem;
    margin: 0.5rem 0;
  }

  li {
    margin: 0.3rem 0;
  }

  strong {
    color: #ffd700;
    font-weight: 600;
  }
`;

const InputContainer = styled.div`
  position: sticky;
  bottom: 0;
  padding: ${glassTheme.spacing.md};
  background: rgba(0, 0, 0, 0.8);
  border-top: 1px solid ${glassTheme.colors.border};

  @media (max-width: 768px) {
    padding: ${glassTheme.spacing.sm};
  }
`;

const InputWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 10px 56px 10px ${glassTheme.spacing.md};
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

  &:focus-within {
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
`;

const ChatInput = styled.textarea`
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  font-family: inherit;
  resize: none;
  min-height: 44px;
  max-height: 160px;
  line-height: 1.5;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SendButton = styled.button<{ $disabled: boolean }>`
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem;
  width: 44px;
  height: 44px;
  background: ${props => props.$disabled ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)'};
  border: none;
  border-radius: 8px;
  color: ${props => props.$disabled ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.85)'};
  font-size: 0.9rem;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: white;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
`;

const Spinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  border-top: 2px solid white;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${glassTheme.colors.text.secondary};
  gap: ${glassTheme.spacing.lg};
  padding: ${glassTheme.spacing.xl};

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: ${glassTheme.colors.text.primary};
    margin-bottom: 0.5rem;
  }
`;

const SuggestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${glassTheme.spacing.sm};
  max-width: 800px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SuggestionCard = styled.button`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.9);
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  min-height: 64px;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }

  .icon {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
  }
`;

const ConfigAPIButton = styled.button`
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
`;

const SocraticToggle = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$active ? 'rgba(102, 126, 234, 0.35)' : 'rgba(255, 255, 255, 0.06)'};
  border: 1px solid ${props => props.$active ? 'rgba(102, 126, 234, 0.7)' : 'rgba(255, 255, 255, 0.15)'};
  color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.75)'};

  &:hover {
    background: rgba(102, 126, 234, 0.25);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
`;

const TopicBrowser = styled.div`
  width: 100%;
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: ${glassTheme.spacing.sm};
`;

const TopicSearchInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.85rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.3);
  color: ${glassTheme.colors.text.primary};
  font-size: 0.9rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.6);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.25);
  }
`;

const CategoryChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  max-height: 116px;
  overflow-y: auto;
`;

const CategoryChip = styled.button<{ $active: boolean }>`
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$active ? 'rgba(102, 126, 234, 0.35)' : 'rgba(255, 255, 255, 0.06)'};
  border: 1px solid ${props => props.$active ? 'rgba(102, 126, 234, 0.7)' : 'rgba(255, 255, 255, 0.12)'};
  color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.75)'};

  &:hover {
    background: rgba(102, 126, 234, 0.25);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.6);
  }
`;

const TopicQuestionList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.45rem;
  max-height: 240px;
  overflow-y: auto;
  padding-right: 0.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TopicQuestion = styled.button`
  padding: 0.6rem 0.8rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.9);
  text-align: left;
  font-size: 0.82rem;
  line-height: 1.45;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(102, 126, 234, 0.2);
    border-color: rgba(102, 126, 234, 0.4);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.6);
  }
`;

const TopicHint = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
`;

export const ChatInterface: React.FC = () => {
  const [input, setInput] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(() => !window.matchMedia('(max-width: 768px)').matches);
  const [showLLMConfig, setShowLLMConfig] = useState(false);
  useLearningTimingPause(showLLMConfig);
  const [topicQuery, setTopicQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sidebarToggleRef = useRef<HTMLButtonElement>(null);

  const {
    currentConversation,
    isLoading,
    streamingMessageId,
    sendMessage,
    abortStreaming,
    socraticMode,
    setSocraticMode,
  } = useChatStore();

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
        sidebarToggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [sidebarOpen]);

  // Fullscreen: cover the whole window and stop the page behind from scrolling,
  // so the conversation is the only scrollable region.
  useEffect(() => {
    if (!isFullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isFullscreen]);

  const handleSend = async () => {
    // If currently streaming, interrupt instead
    if (streamingMessageId) {
      abortStreaming();
      return;
    }

    if (!input.trim() || isLoading) return;

    const messageToSend = input.trim();
    setInput('');

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    await sendMessage(messageToSend);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestion = (text: string) => {
    setInput(text);
    textareaRef.current?.focus();
  };

  // Search wins over the selected category; with neither, show curated starters.
  const normalizedTopicQuery = topicQuery.trim().toLowerCase();
  const browsedTopics = (() => {
    if (normalizedTopicQuery) {
      const hits: string[] = [];
      STATS_TOPIC_CATEGORIES.forEach(category => {
        const titleMatches = category.title.toLowerCase().includes(normalizedTopicQuery);
        category.topics.forEach(topic => {
          if (titleMatches || topic.toLowerCase().includes(normalizedTopicQuery)) {
            hits.push(topic);
          }
        });
      });
      return hits.slice(0, 60);
    }
    if (activeCategory) {
      const category = STATS_TOPIC_CATEGORIES.find(c => c.title === activeCategory);
      return category ? category.topics : [];
    }
    return STARTER_TOPICS;
  })();

  const chatContent = (
    <ChatWrapper $sidebarOpen={sidebarOpen} $fullscreen={isFullscreen}>
      <ChatSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      {sidebarOpen && (
        <MobileSidebarBackdrop
          aria-label="关闭对话记录"
          onClick={() => {
            setSidebarOpen(false);
            sidebarToggleRef.current?.focus();
          }}
        />
      )}

      <ChatMainArea $sidebarOpen={sidebarOpen}>
        <TitleBar>
          <TitleBarLeft>
            {!sidebarOpen && (
              <CollapseSidebarButton ref={sidebarToggleRef} onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="打开对话记录">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <path fill="currentColor" fillRule="evenodd" d="M21 5H11v14h10zM3 5h6v14H3zm0-2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm2 4.25a.75.75 0 0 0 0 1.5h2a.75.75 0 0 0 0-1.5zm-.75 3.5A.75.75 0 0 1 5 10h2a.75.75 0 0 1 0 1.5H5a.75.75 0 0 1-.75-.75m.75 2a.75.75 0 0 0 0 1.5h2a.75.75 0 0 0 0-1.5z" clipRule="evenodd"></path>
                </svg>
              </CollapseSidebarButton>
            )}
          </TitleBarLeft>

          <TitleBarCenter>
            <TitleBarTitle>统计助教</TitleBarTitle>
          </TitleBarCenter>

          <TitleBarRight>
            <SocraticToggle
              type="button"
              $active={isFullscreen}
              aria-pressed={isFullscreen}
              onClick={() => setIsFullscreen(v => !v)}
              title={isFullscreen
                ? '退出专注模式，回到页面内的对话框（也可按 Esc 退出）'
                : '专注模式：让统计助教全屏覆盖整个窗口，一次能看到更多内容，不用来回滚动'}
            >
              {isFullscreen ? '⤡ 退出专注' : '⤢ 专注模式'}
            </SocraticToggle>
            <SocraticToggle
              type="button"
              $active={socraticMode}
              aria-pressed={socraticMode}
              onClick={() => setSocraticMode(!socraticMode)}
              title={socraticMode
                ? '当前：启发模式 —— 助教先用问题引导你自己推导，暂不直接给答案；点击可切回「讲解模式」，直接得到完整讲解'
                : '当前：讲解模式 —— 助教直接给出完整讲解和公式；点击可切换到「启发模式」，让助教先提问引导你思考，更适合复习和自我检验'}
            >
              {socraticMode ? '🧠 启发模式' : '💡 讲解模式'}
            </SocraticToggle>
            <ConfigAPIButton
              onClick={() => setShowLLMConfig(true)}
              title="配置AI：选择你要使用的大模型服务商（如 DeepSeek、通义千问等）并填写API密钥；如果助教无法回复，通常需要在这里检查配置"
            >
              <i className="fas fa-cog"></i>
              配置AI
            </ConfigAPIButton>
          </TitleBarRight>
        </TitleBar>

        <MessagesContainer role="log" aria-live="polite" aria-relevant="additions">
          {(!currentConversation || currentConversation.messages.length === 0) && (
            <EmptyState>
              <h1>想了解什么统计学问题？</h1>
              <TopicBrowser>
                <TopicSearchInput
                  type="search"
                  value={topicQuery}
                  onChange={e => setTopicQuery(e.target.value)}
                  placeholder={`搜索 ${STATS_TOPIC_TOTAL} 个统计学主题…`}
                  aria-label="搜索统计学主题"
                />
                <CategoryChipRow>
                  {STATS_TOPIC_CATEGORIES.map(category => (
                    <CategoryChip
                      key={category.title}
                      type="button"
                      $active={activeCategory === category.title}
                      onClick={() => setActiveCategory(
                        activeCategory === category.title ? null : category.title
                      )}
                    >
                      {category.title}
                    </CategoryChip>
                  ))}
                </CategoryChipRow>
                <TopicHint>
                  {browsedTopics.length > 0
                    ? (topicQuery.trim() || activeCategory
                        ? `${browsedTopics.length} 个相关主题`
                        : '点击上方分类浏览主题，或直接提问')
                    : '未找到相关主题，试试其他关键词'}
                </TopicHint>
                <TopicQuestionList>
                  {browsedTopics.map(topic => (
                    <TopicQuestion
                      key={topic}
                      type="button"
                      onClick={() => handleSuggestion(buildTopicRequest(topic))}
                    >
                      {topic}
                    </TopicQuestion>
                  ))}
                </TopicQuestionList>
              </TopicBrowser>
            </EmptyState>
          )}
          {currentConversation?.messages.map((message) => {
            const isUser = message.sender === 'user';
            return (
              <MessageBubble key={message.id} $isUser={isUser} aria-label={isUser ? '您' : '统计助教'}>
                <MarkdownRenderer content={message.content} fontSize="1.05rem" />
              </MessageBubble>
            );
          })}
          {isLoading && (
            <MessageBubble $isUser={false}>
              <><Spinner aria-hidden="true" /><span>AI正在生成回复…</span></>
            </MessageBubble>
          )}
          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputContainer>
          <InputWrapper>
            <ChatInput
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="向统计助教提问..."
              aria-label="向统计助教提问"
              disabled={isLoading}
            />
            <SendButton
              onClick={handleSend}
              $disabled={!input.trim() || (isLoading && !streamingMessageId)}
              disabled={!streamingMessageId && (!input.trim() || isLoading)}
              aria-label={streamingMessageId ? "停止生成" : "发送消息"}
            >
              {streamingMessageId ? (
                <i className="fas fa-stop"></i>
              ) : isLoading ? (
                <Spinner />
              ) : (
                <i className="fas fa-arrow-up"></i>
              )}
            </SendButton>
          </InputWrapper>
        </InputContainer>
      </ChatMainArea>

      <LLMConfigModal
        visible={showLLMConfig}
        onClose={() => setShowLLMConfig(false)}
        onConfigSaved={() => {
          window.location.reload();
        }}
      />
    </ChatWrapper>
  );

  // The learning platform wraps this in a backdrop-filter card, which becomes the
  // containing block for position:fixed. Portalling to body is what makes the
  // fullscreen overlay actually cover the viewport instead of the card.
  return isFullscreen ? createPortal(chatContent, document.body) : chatContent;
};

export default ChatInterface;
